import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as Styles from './class-code-field.module.scss';
import { useSite } from '../contexts/SiteContext';

function ClassCodeField({ onValidCode, onInvalidCode, onBlankCode, defaultValue, required, id, className }) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputElement = useRef(null);
  const { canJoinClassroom } = useSite();

  async function isValidCode(code) {
    if (code.length === 0) {
      return null;
    }

    if (code.match(/[^0-9a-fA-F]/i)) {
      throw new Error('Class codes only contain numbers and letters A-F.');
    }

    if (code.length !== 6) {
      throw new Error('Class code must be 6 characters long.');
    }

    return canJoinClassroom(code.toLowerCase());
  }

  async function handleChange(code) {
    setValue(code.toUpperCase());

    if (code.length === 0) {
      setError(null);
      inputElement.current.setCustomValidity('');
      onBlankCode();
      return;
    }

    setLoading(true);
    inputElement.current.setCustomValidity('Verifying...');
    try {
      await isValidCode(code);
      inputElement.current.setCustomValidity('');
      setError(null);
      onValidCode(code);
    } catch (e) {
      setError(e.details || e.message || 'Invalid code');
      if (inputElement.current) {
        inputElement.current.setCustomValidity(e.message);
      }
      onInvalidCode(code);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (defaultValue && inputElement.current) {
      handleChange(inputElement.current.value);
    }
  }, [inputElement]);

  function state() {
    if (error) {
      return 'error';
    }

    if (value && value.length === 6) {
      return 'success';
    }

    return null;
  }

  return (
    <div className={classNames(className, Styles.root)} data-state={state()}>
      <div className={Styles.fieldWrapper}>
        <input
          id={id}
          type="text"
          maxLength="6"
          ref={inputElement}
          required={required}
          onChange={(e) => handleChange(e.target.value)}
          value={value}
          className={classNames('form-control', Styles.field)}
        />
      </div>
      <div className={Styles.messages}>
        {value && value.length === 6 && !error && !loading && (
          <div className={Styles.success}>Class code verified!</div>
        )}
        {error && !loading && <div className={Styles.error}>{error}</div>}
        {loading && <div>Verifying...</div>}
      </div>
    </div>
  );
}

ClassCodeField.propTypes = {
  onValidCode: PropTypes.func,
  onInvalidCode: PropTypes.func,
  onBlankCode: PropTypes.func,
  defaultValue: PropTypes.string,
  required: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
};

ClassCodeField.defaultProps = {
  onValidCode: () => {},
  onInvalidCode: () => {},
  onBlankCode: () => {},
  defaultValue: '',
  required: false,
  id: null,
  className: null,
};

export default ClassCodeField;
